import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

export interface TourStep {
  /** CSS selector for the target element */
  target: string;
  /** Title shown in the tooltip */
  title: string;
  /** Description shown in the tooltip */
  description: string;
  /** Preferred placement of the tooltip */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Optional: navigate to this path before showing the step */
  navigateTo?: string;
}

export interface TourDefinition {
  id: string;
  steps: TourStep[];
}

interface TourContextValue {
  isActive: boolean;
  currentStepIndex: number;
  currentStep: TourStep | null;
  totalSteps: number;
  startTour: (tour: TourDefinition) => void;
  nextStep: () => void;
  prevStep: () => void;
  endTour: () => void;
  tourId: string | null;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const [tourId, setTourId] = useState<string | null>(null);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const isActive = tourId !== null;
  const currentStep = isActive ? steps[currentStepIndex] ?? null : null;

  const startTour = useCallback((tour: TourDefinition) => {
    setTourId(tour.id);
    setSteps(tour.steps);
    setCurrentStepIndex(0);
  }, []);

  const endTour = useCallback(() => {
    if (tourId) {
      localStorage.setItem(`tour_completed_${tourId}`, '1');
    }
    setTourId(null);
    setSteps([]);
    setCurrentStepIndex(0);
  }, [tourId]);

  const nextStep = useCallback(() => {
    setCurrentStepIndex(prev => {
      if (prev >= steps.length - 1) {
        // End tour on last step
        setTimeout(endTour, 0);
        return prev;
      }
      return prev + 1;
    });
  }, [steps.length, endTour]);

  const prevStep = useCallback(() => {
    setCurrentStepIndex(prev => Math.max(0, prev - 1));
  }, []);

  return (
    <TourContext.Provider value={{
      isActive,
      currentStepIndex,
      currentStep,
      totalSteps: steps.length,
      startTour,
      nextStep,
      prevStep,
      endTour,
      tourId,
    }}>
      {children}
    </TourContext.Provider>
  );
}
